import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { axiosWCApiInstance } from '../../configuration/axios/axiosConfiguration';
import { appInsights } from '../../AppInsights';
import { ApiError } from '../../utils/ApiError';
import { downloadBlobFile, sleep } from '../../utils/helpers';

// TODO: this should be replaced when we wire up TS lists pages
const getTimesheetsCountByDivision = async () => {
    try {
        await sleep(2000);
        return [
            {
                label: 'Anesthesia',
                id: 'anesthesia',
                count: 78,
            },
            {
                label: 'Emergency Medicine',
                id: 'emergencyMedicine',
                count: 23,
            },
            {
                label: 'Government',
                id: 'government',
                count: 14,
            },
            {
                label: 'Hospital Medicine',
                id: 'hospitalMedicine',
                count: 12,
            },
            {
                label: 'Internal Medicine Sub-Specialties',
                id: 'internalMedicineSubSpecialties',
                count: 8,
            },
            {
                label: 'Neurology',
                id: 'neurology',
                count: 74,
            },
            {
                label: 'Oncology',
                id: 'oncology',
                count: 22,
            },
            {
                label: 'Primary Care',
                id: 'primaryCare',
                count: 4,
            },
            {
                label: 'Psychiatry',
                id: 'psychiatry',
                count: 33,
            },
            {
                label: 'Radiology',
                id: 'radiology',
                count: 50,
            },
            {
                label: 'Sugery - Clinic-Based',
                id: 'surgeryClinic',
                count: 12,
            },
            {
                label: 'Sugery - Hospital-Based',
                id: 'surgeryHospital',
                count: 87,
            },
        ];
    } catch (error) {
        console.error(error);
        throw error;
    }
};

const getProcessedTimesheets = async (params) => {
    try {
        const response = await axiosWCApiInstance.get('processedTimesheets', { params });
        return response.data;
    } catch (error) {
        console.log(`GetProcessedTimesheets error=${error}`);
        appInsights.trackException({
            exception: error,
            properties: {
                method: 'getProcessedTimesheets',
                severityLevel: SeverityLevel.Error,
            },
        });
        if (error.response?.data) {
            throw new ApiError(error.response.data);
        } else if (error.message) {
            throw new ApiError(error.message);
        } else {
            throw ApiError.UnwrappedError(
                'Server Error',
                'There was a problem processing that request.',
                'GetProcessedTimesheets'
            );
        }
    }
};

const getProcessedTimesheetDetails = async ({
    processedTimesheetId,
    followToAggregate = false,
}) => {
    try {
        const response = await axiosWCApiInstance.get(
            `processedTimesheetDetails/${processedTimesheetId}?followToAggregate=${followToAggregate}`
        );
        return response.data;
    } catch (error) {
        console.log(`GetProcessedTimesheetDetails error=${error}`);
        appInsights.trackException({
            exception: error,
            properties: {
                method: 'getProcessedTimesheetDetails',
                severityLevel: SeverityLevel.Error,
            },
        });
        if (error.response?.data) {
            throw new ApiError(error.response.data);
        } else if (error.message) {
            throw new ApiError(error.message);
        } else {
            throw ApiError.UnwrappedError(
                'Server Error',
                'There was a problem processing that request.',
                'GetProcessedTimesheetDetails'
            );
        }
    }
};

const getActivityFeed = async ({ processedTimesheetId }) => {
    try {
        const response = await axiosWCApiInstance.get(`ActivityFeed/${processedTimesheetId}`);
        return response.data;
    } catch (error) {
        console.log(`GetActivityFeed error=${error}`);
        appInsights.trackException({
            exception: error,
            properties: {
                method: 'getActivityFeed',
                severityLevel: SeverityLevel.Error,
            },
        });
        if (error.response?.data) {
            throw new ApiError(error.response.data);
        } else if (error.message) {
            throw new ApiError(error.message);
        } else {
            throw ApiError.UnwrappedError(
                'Server Error',
                'There was a problem processing that request.',
                'GetProcessedTimesheets'
            );
        }
    }
};

const patchProcessedTimesheetStatus = async ({
    processedTimesheetId,
    newStatus,
    statusChangeReason,
    lastModified,
}) => {
    try {
        const response = await axiosWCApiInstance.patch(
            'processedTimesheet/statusChange',
            { processedTimesheetId, newStatus, statusChangeReason, lastModified },
            { headers: { 'LT-Roles': 'Admin' } }
        );
        return response.data;
    } catch (error) {
        console.log(`PatchProcessedTimesheetStatus error=${error}`);
        appInsights.trackException({
            exception: error,
            properties: {
                method: 'patchProcessedTimesheetStatus',
                severityLevel: SeverityLevel.Error,
            },
        });
        if (error.response?.data) {
            throw new ApiError({
                detail: error.response?.data,
                status: error.response?.status,
            });
        } else if (error.message) {
            throw new ApiError(error.message);
        } else {
            throw ApiError.UnwrappedError(
                'Server Error',
                'There was a problem processing that request.',
                'PatchProcessedTimesheetStatus'
            );
        }
    }
};

const patchProcessedTimesheetHeader = async ({
    processedTimesheetId,
    payrollDate,
    approvalDate,
    monthPosted,
    accountingCase,
    lastModified,
}) => {
    try {
        const response = await axiosWCApiInstance.patch(
            `processedTimesheet/patchProcessedTimesheetHeader`,
            {
                processedTimesheetId,
                payrollDate,
                approvalDate,
                monthPosted,
                accountingCase,
                lastModified,
            }
        );
        return response.data;
    } catch (error) {
        console.log(`patchProcessedTimesheetHeader error=${error}`);
        appInsights.trackException({
            exception: error,
            properties: {
                method: 'patchProcessedTimesheetHeader',
                severityLevel: SeverityLevel.Error,
            },
        });
        if (error.response?.data) {
            throw new ApiError({
                detail: error.response.data,
                status: error.response?.status,
            });
        } else if (error.message) {
            throw new ApiError(error.message);
        } else {
            throw ApiError.UnwrappedError(
                'Server Error',
                'There was a problem processing that request.',
                'patchProcessedTimesheetHeader'
            );
        }
    }
};

const postActivityFeedComment = async ({ objectId, formattedCommentContent, type = 'Comment' }) => {
    try {
        const response = await axiosWCApiInstance.post('activityFeed', {
            objectId,
            formattedCommentContent,
            type,
        });
        return response.data;
    } catch (error) {
        console.log(`PostActivityFeedComment error=${error}`);
        appInsights.trackException({
            exception: error,
            properties: {
                method: 'postActivityFeedComment',
                severityLevel: SeverityLevel.Error,
            },
        });
        if (error.response?.data) {
            throw new ApiError(error.response.data);
        } else if (error.message) {
            throw new ApiError(error.message);
        } else {
            throw ApiError.UnwrappedError(
                'Server Error',
                'There was a problem processing that request.',
                'PostActivityFeedComment'
            );
        }
    }
};

const getTimesheetPDFByLocation = async ({
    clinicianId,
    locationId,
    locationName,
    clinicianName,
    weekEndingDate,
    rawTimesheetId,
}) => {
    try {
        const response = await axiosWCApiInstance.get(
            `/clinicians/${clinicianId}/timesheets/${rawTimesheetId}/pdf/${locationId}`,
            { responseType: 'blob' }
        );
        const fileName = `${clinicianName} - ${locationName} - ${weekEndingDate}.pdf`;
        downloadBlobFile({ fileName, blobData: response.data });
        return true;
    } catch (error) {
        console.error(`getTimesheetPDFByLocation error=${error}`);
        appInsights.trackException({
            exception: error,
            properties: {
                method: 'getTimesheetPDFByLocation',
                severityLevel: SeverityLevel.Error,
            },
        });
        if (error.response?.data) {
            throw new ApiError(error.response.data);
        } else if (error.message) {
            throw new ApiError(error.message);
        } else {
            throw ApiError.UnwrappedError(
                'Server Error',
                'There was a problem retrieving the PDF file.',
                'getTimesheetPDFByLocation'
            );
        }
    }
};

const getProcessedTimesheetAttachments = async ({ processedTimesheetId }) => {
    try {
        const response = await axiosWCApiInstance.get(`processedTimesheet/getAttachments`, {
            params: { processedTimesheetId },
        });
        return response.data;
    } catch (error) {
        console.log(`getAttachments error=${error}`);
        appInsights.trackException({
            exception: error,
            properties: {
                method: 'getAttachments',
                severityLevel: SeverityLevel.Error,
            },
        });
        if (error.response?.data) {
            throw new ApiError(error.response.data);
        } else if (error.message) {
            throw new ApiError(error.message);
        } else {
            throw ApiError.UnwrappedError(
                'Server Error',
                'There was a problem processing that request.',
                'getAttachments'
            );
        }
    }
};

const downloadAttachmentById = async ({ attachmentId, source }) => {
    try {
        const response = await axiosWCApiInstance.get(
            `processedTimesheet/getAttachmentById?attachmentId=${attachmentId}&source=${source}`
        );
        return response.data;
    } catch (error) {
        console.log(`getAttachmentById error=${error}`);
        appInsights.trackException({
            exception: error,
            properties: { method: 'getAttachmentById', severityLevel: SeverityLevel.Error },
        });
        if (error instanceof ApiError) {
            throw error;
        } else if (error.response?.data) {
            throw new ApiError(error.response.data);
        } else if (error.message) {
            throw new ApiError(error.message);
        } else {
            throw ApiError.UnwrappedError(
                'Server Error',
                'There was a problem processing that request.',
                'getAttachmentById'
            );
        }
    }
};

const postAttachment = async ({
    processedTimesheetId,
    rawTimesheetId,
    file,
    onUploadProgress,
    signal,
}) => {
    try {
        const form = new FormData();
        form.append('attachment', file, file.name);

        const response = await axiosWCApiInstance.post(
            `processedTimesheet/uploadAttachment?rawTimesheetId=${rawTimesheetId}&processedTimesheetId=${processedTimesheetId}`,
            form,
            { headers: { 'LT-Roles': 'TimesheetAdmin' }, onUploadProgress, signal }
        );
        return response.data;
    } catch (error) {
        appInsights.trackException({
            exception: error,
            properties: { method: 'uploadAttachment', severityLevel: SeverityLevel.Error },
        });
        if (error.response) {
            throw ApiError.ApiFormattedError(error.response);
        }
        throw ApiError.UnwrappedError(
            'Server Error',
            'There was a problem processing that request.',
            'uploadAttachment'
        );
    }
};

const deleteAttachment = async ({ processedTimesheetId, rawTimesheetId, fileName }) => {
    try {
        const requestBody = {
            processedTimesheetId: Number(processedTimesheetId),
            rawTimesheetId,
            name: fileName,
        };
        const response = await axiosWCApiInstance.delete(
            'processedTimesheet/deleteAttachment',
            { data: requestBody },
            { headers: { 'LT-Roles': 'TimesheetAdmin' } }
        );
        return response.data;
    } catch (error) {
        appInsights.trackException({
            exception: error,
            properties: { method: 'deleteAttachment', severityLevel: SeverityLevel.Error },
        });
        if (error.response) {
            throw ApiError.ApiFormattedError(error.response);
        }
        throw ApiError.UnwrappedError(
            'Server Error',
            'There was a problem processing that request.',
            'deleteAttachment'
        );
    }
};

const patchAttachmentTypeAndLocation = async ({ id, locationId, documentType, lastModified }) => {
    try {
        const response = await axiosWCApiInstance.put(
            `processedTimesheet/setAttachmentTypeAndLocation`,
            {
                id,
                lastModified,
                ...(locationId !== undefined && { locationId: locationId || null }),
                ...(documentType !== undefined && { documentType: documentType || null }),
            }
        );
        return response.data;
    } catch (error) {
        console.log(`setAttachmentTypeAndLocation error=${error}`);
        appInsights.trackException({
            exception: error,
            properties: {
                method: 'setAttachmentTypeAndLocation',
                severityLevel: SeverityLevel.Error,
            },
        });
        if (error.response?.data) {
            throw new ApiError(error.response.data);
        } else if (error.message) {
            throw new ApiError(error.message);
        } else {
            throw ApiError.UnwrappedError(
                'Server Error',
                'There was a problem processing that request.',
                'setAttachmentTypeAndLocation'
            );
        }
    }
};

const postProcessedTimesheetEntry = async (options) => {
    try {
        const response = await axiosWCApiInstance.post(
            'processedTimesheet/postProcessedTimesheetEntry',
            options
        );
        return response.data;
    } catch (error) {
        console.log(`postProcessedTimesheetEntry error=${error}`);
        appInsights.trackException({
            exception: error,
            properties: {
                method: 'postProcessedTimesheetEntry',
                severityLevel: SeverityLevel.Error,
            },
        });
        if (error.response?.data) {
            throw new ApiError({
                detail: error.response.data,
                status: error.response?.status,
            });
        } else if (error.message) {
            throw new ApiError(error.message);
        } else {
            throw ApiError.UnwrappedError(
                'Server Error',
                'There was a problem processing that request.',
                'postProcessedTimesheetEntry'
            );
        }
    }
};

const patchProcessedTimesheetEntry = async (options) => {
    try {
        const response = await axiosWCApiInstance.patch(
            'processedTimesheet/patchProcessedTimesheetEntry',
            options
        );
        return response.data;
    } catch (error) {
        console.log(`PatchProcessedTimesheetEntry error=${error}`);
        appInsights.trackException({
            exception: error,
            properties: {
                method: 'patchProcessedTimesheetEntry',
                severityLevel: SeverityLevel.Error,
            },
        });
        if (error.response?.data) {
            throw new ApiError({
                detail: error.response.data,
                status: error.response?.status,
            });
        } else if (error.message) {
            throw new ApiError(error.message);
        } else {
            throw ApiError.UnwrappedError(
                'Server Error',
                'There was a problem processing that request.',
                'PatchProcessedTimesheetEntry'
            );
        }
    }
};

const deleteProcessedTimesheetEntry = async ({ id, isMileage = false, lastModified }) => {
    try {
        const response = await axiosWCApiInstance.delete(
            `processedTimesheet/processedTimesheetEntry/${id}?isMileage=${isMileage}&lastModified=${lastModified}`
        );
        return response.data;
    } catch (error) {
        console.log(`DeleteProcessedTimesheetEntry error=${error}`);
        appInsights.trackException({
            exception: error,
            properties: {
                method: 'deleteProcessedTimesheetEntry',
                severityLevel: SeverityLevel.Error,
            },
        });
        if (error.response?.data) {
            throw new ApiError({
                detail: error.response.data,
                status: error.response?.status,
            });
        } else if (error.message) {
            throw new ApiError(error.message);
        } else {
            throw ApiError.UnwrappedError(
                'Server Error',
                'There was a problem processing that request.',
                'DeleteProcessedTimesheetEntry'
            );
        }
    }
};

const postNewTimesheetVersion = async ({ processedTimesheetId, formattedCommentContent }) => {
    try {
        const response = await axiosWCApiInstance.post(`processedTimesheet/newversion`, {
            processedTimesheetId,
            formattedCommentContent,
        });
        return response.data;
    } catch (error) {
        console.log(`PostNewTimesheetVersion error=${error}`);
        appInsights.trackException({
            exception: error,
            properties: {
                method: 'PostNewTimesheetVersion',
                severityLevel: SeverityLevel.Error,
            },
        });
        if (error.response?.data) {
            throw new ApiError({
                detail: error.response.data,
                status: error.response?.status,
            });
        } else if (error.message) {
            throw new ApiError(error.message);
        } else {
            throw ApiError.UnwrappedError(
                'Server Error',
                'There was a problem processing that request.',
                'PostNewTimesheetVersion'
            );
        }
    }
};

const getAssociatedInvoices = async ({ id }) => {
    try {
        const response = await axiosWCApiInstance.get(
            `processedTimesheet/${id}/associatedInvoices`
        );
        return response.data;
    } catch (error) {
        console.log(`GetAssociatedInvoices error=${error}`);
        appInsights.trackException({
            exception: error,
            properties: {
                method: 'getAssociatedInvoices',
                severityLevel: SeverityLevel.Error,
            },
        });
        if (error.response?.data) {
            throw new ApiError({
                detail: error.response.data,
                status: error.response?.status,
            });
        } else if (error.message) {
            throw new ApiError(error.message);
        } else {
            throw ApiError.UnwrappedError(
                'Server Error',
                'There was a problem processing that request.',
                'GetAssociatedInvoices'
            );
        }
    }
};

const getInvoiceDocuments = async ({ processedTimesheetId, includeErrored = false }) => {
    try {
        const response = await axiosWCApiInstance.get(
            `processedTimesheet/${processedTimesheetId}/invoiceDocuments`,
            { params: { includeErrored } }
        );
        return response.data;
    } catch (error) {
        console.log(`GetInvoiceDocuments error=${error}`);
        appInsights.trackException({
            exception: error,
            properties: {
                method: 'getInvoiceDocuments',
                severityLevel: SeverityLevel.Error,
            },
        });
        if (error.response?.data) {
            throw new ApiError({
                detail: error.response.data,
                status: error.response?.status,
            });
        } else if (error.message) {
            throw new ApiError(error.message);
        } else {
            throw ApiError.UnwrappedError(
                'Server Error',
                'There was a problem processing that request.',
                'GetInvoiceDocuments'
            );
        }
    }
};

const getProcessedTimesheetHistory = async (processedTimesheetId) => {
    try {
        const response = await axiosWCApiInstance.get(
            `processedTimesheet/${processedTimesheetId}/history`
        );
        return response.data || [];
    } catch (error) {
        console.log(`GetProcessedTimesheetHistory error=${error}`);
        appInsights.trackException({
            exception: error,
            properties: {
                method: 'getProcessedTimesheetHistory',
                severityLevel: SeverityLevel.Error,
            },
        });
        if (error.response?.data) {
            throw new ApiError({
                detail: error.response.data,
                status: error.response?.status,
            });
        } else if (error.message) {
            throw new ApiError(error.message);
        } else {
            throw ApiError.UnwrappedError(
                'Server Error',
                'There was a problem processing that request.',
                'GetProcessedTimesheetHistory'
            );
        }
    }
};

const patchCreditMemoMode = async ({ processedTimesheetId, creditMemoMode }) => {
    try {
        const response = await axiosWCApiInstance.patch(
            `${processedTimesheetId}/CreditMemoMode`,
            creditMemoMode,
            { headers: { 'Content-Type': 'application/json' }, params: { isExpenseId: false } }
        );
        return response.data;
    } catch (error) {
        console.log(`PatchCreditMemoMode error=${error}`);
        appInsights.trackException({
            exception: error,
            properties: {
                method: 'patchCreditMemoMode',
                severityLevel: SeverityLevel.Error,
            },
        });
        if (error.response?.data) {
            throw new ApiError({
                detail: error.response.data,
                status: error.response?.status,
            });
        } else if (error.message) {
            throw new ApiError(error.message);
        } else {
            throw ApiError.UnwrappedError(
                'Server Error',
                'There was a problem processing that request.',
                'PatchCreditMemoMode'
            );
        }
    }
};

const getProcessedTimesheetStatuses = async () => {
    try {
        const response = await axiosWCApiInstance.get('processedTimesheet/statuses');
        return response.data;
    } catch (error) {
        console.log(`GetProcessedTimesheetStatuses error=${error}`);
        appInsights.trackException({
            exception: error,
            properties: {
                method: 'getProcessedTimesheetStatuses',
                severityLevel: SeverityLevel.Error,
            },
        });
        if (error.response?.data) {
            throw new ApiError(error.response.data);
        } else if (error.message) {
            throw new ApiError(error.message);
        } else {
            throw ApiError.UnwrappedError(
                'Server Error',
                'There was a problem processing that request.',
                'GetProcessedTimesheetStatuses'
            );
        }
    }
};

export {
    getTimesheetsCountByDivision,
    getProcessedTimesheets,
    getProcessedTimesheetDetails,
    getActivityFeed,
    patchProcessedTimesheetStatus,
    patchProcessedTimesheetHeader,
    postActivityFeedComment,
    getTimesheetPDFByLocation,
    getProcessedTimesheetAttachments,
    downloadAttachmentById,
    postAttachment,
    deleteAttachment,
    patchAttachmentTypeAndLocation,
    postProcessedTimesheetEntry,
    patchProcessedTimesheetEntry,
    deleteProcessedTimesheetEntry,
    getAssociatedInvoices,
    getInvoiceDocuments,
    getProcessedTimesheetHistory,
    patchCreditMemoMode,
    getProcessedTimesheetStatuses,
    postNewTimesheetVersion,
};
